import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from '../http/http.service';
import { leerDeLaPestana } from '../auth/sesion-pestana.storage';

/** Una fila del maestro de parametros de Simappe Admin. */
interface TemplateParameter {
  id: number;
  name: string;
  defaultValue: string | null;
}

/**
 * Una fila de parametros de empresa. El API entrega el identificador del
 * maestro pero no su nombre — `templateParameters` viaja siempre nulo —, de modo
 * que el cruce por `templateParametersId` es obligatorio y no un atajo.
 */
interface CompanyParameter {
  templateParametersId: number | null;
  value: string | null;
}

/**
 * Lee el valor de un parametro de empresa de Simappe Admin.
 *
 * Es el cruce que hace falta para cualquier parametro: el maestro da el nombre y
 * el valor por defecto; la empresa, su valor propio si lo tiene. Manda el de la
 * empresa y, si no lo hay, el del maestro, que es donde vive el valor comun a
 * todas.
 *
 * El nombre de cada parametro lo declara quien lo usa, del lado de Nebula, y no
 * el enum de la linea Simappe, que no se toca.
 */
@Injectable({ providedIn: 'root' })
export class CompanyParameterReader {
  private readonly http = inject(HttpService);

  private static readonly TEMPLATES_ENDPOINT = '/simappe-admin/api/v1/template-parameters/read';
  private static readonly COMPANY_ENDPOINT =
    '/simappe-admin/api/v1/company-parameters/read-company';

  /**
   * Valor vigente de un parametro para la compania de la sesion.
   *
   * Nunca propaga error: si el maestro no responde, o el parametro no existe,
   * devuelve nulo y quien lo usa conserva lo que ya tenia.
   *
   * @param nombre el nombre del parametro en el maestro.
   * @returns el valor, o nulo si no se pudo saber.
   */
  valor(nombre: string): Observable<string | null> {
    const companyId = this.companyId();
    if (companyId == null) return of(null);

    return forkJoin({
      templates: this.http
        .getContent<TemplateParameter[]>(CompanyParameterReader.TEMPLATES_ENDPOINT)
        .pipe(catchError(() => of([] as TemplateParameter[]))),
      parameters: this.http
        .getContent<CompanyParameter[]>(CompanyParameterReader.COMPANY_ENDPOINT, {
          companyId: String(companyId),
        })
        .pipe(catchError(() => of([] as CompanyParameter[]))),
    }).pipe(
      map(({ templates, parameters }) => {
        const template = (templates ?? []).find((t) => t?.name === nombre);
        if (!template) return null;
        const asignado = (parameters ?? []).find((p) => p?.templateParametersId === template.id);
        return asignado?.value ?? template.defaultValue ?? null;
      }),
      catchError(() => of(null)),
    );
  }

  /** Compania de la sesion, o nulo si aun no la hay. */
  private companyId(): number | null {
    try {
      const raw = leerDeLaPestana('user_data');
      if (!raw) return null;
      const id = JSON.parse(raw)?.companyId;
      return typeof id === 'number' ? id : null;
    } catch {
      return null;
    }
  }
}
